/**
 * HaulerCentral Auth Mutation Hooks
 *
 * TanStack Query mutation hooks for HaulerCentral authentication.
 * Following patterns from @commander/auth.
 */

import { type UseMutationOptions, useMutation, useQuery } from '@tanstack/react-query';
import * as authApi from '../services/authApi';
import { useHaulerCentralAuthStore } from '../stores/authStore';
import type {
  AuthResponse,
  ForgotPasswordPayload,
  LoginPayload,
  RegisterPayload,
  ResetPasswordPayload,
  UpdateProfilePayload,
  VerifyEmailPayload,
} from '../types/auth.types';

/**
 * Login mutation hook
 */
export function useLogin(
  options?: Omit<UseMutationOptions<AuthResponse, Error, LoginPayload>, 'mutationFn'>
) {
  const store = useHaulerCentralAuthStore();

  return useMutation<AuthResponse, Error, LoginPayload>({
    mutationFn: (data: LoginPayload) => authApi.login(data),
    onSuccess: (data) => {
      if (data.success && data.data?.user) {
        store.setUser(data.data.user);
        store.setIsAuthenticated(!!data.data.accessToken);
      }
    },
    ...options,
  });
}

/**
 * Register mutation hook
 */
export function useRegister(
  options?: Omit<UseMutationOptions<AuthResponse, Error, RegisterPayload>, 'mutationFn'>
) {
  const store = useHaulerCentralAuthStore();

  return useMutation<AuthResponse, Error, RegisterPayload>({
    mutationFn: (data: RegisterPayload) => authApi.register(data),
    onSuccess: (data) => {
      if (data.success && data.data?.user) {
        store.setUser(data.data.user);
        store.setIsAuthenticated(!!data.data.accessToken);
      }
    },
    ...options,
  });
}

/**
 * Logout mutation hook
 */
export function useLogout(options?: Omit<UseMutationOptions<void, Error, void>, 'mutationFn'>) {
  const store = useHaulerCentralAuthStore();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onMutate: () => {
      // Clear state immediately on logout
      store.setUser(null);
      store.setIsAuthenticated(false);
    },
    ...options,
  });
}

/**
 * Forgot password mutation hook
 */
export function useForgotPassword(
  options?: Omit<UseMutationOptions<any, Error, ForgotPasswordPayload>, 'mutationFn'>
) {
  return useMutation({
    mutationFn: (data: ForgotPasswordPayload) => authApi.forgotPassword(data),
    ...options,
  });
}

/**
 * Reset password mutation hook
 */
export function useResetPassword(
  options?: Omit<UseMutationOptions<any, Error, ResetPasswordPayload>, 'mutationFn'>
) {
  return useMutation({
    mutationFn: (data: ResetPasswordPayload) => authApi.resetPassword(data),
    ...options,
  });
}

/**
 * Verify email mutation hook
 */
export function useVerifyEmail(
  options?: Omit<UseMutationOptions<any, Error, VerifyEmailPayload>, 'mutationFn'>
) {
  return useMutation({
    mutationFn: (payload: VerifyEmailPayload) => authApi.verifyEmail(payload),
    ...options,
  });
}

/**
 * Resend verification email mutation hook
 */
export function useResendVerification(
  options?: Omit<UseMutationOptions<any, Error, string>, 'mutationFn'>
) {
  return useMutation({
    mutationFn: (email: string) => authApi.resendVerificationEmail(email),
    ...options,
  });
}

/**
 * Update profile mutation hook
 */
export function useUpdateProfile(
  options?: Omit<UseMutationOptions<any, Error, UpdateProfilePayload>, 'mutationFn'>
) {
  const store = useHaulerCentralAuthStore();

  return useMutation({
    mutationFn: (data: UpdateProfilePayload) => authApi.updateProfile(data),
    onSuccess: (data) => {
      if (data.success && data.data && store.user) {
        store.updateUser(data.data);
      }
    },
    ...options,
  });
}

/**
 * 2FA status query hook
 */
export function use2FAStatus(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['haulercentral', '2fa-status'],
    queryFn: () => authApi.get2FAStatus(),
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

/**
 * Enable 2FA mutation hook
 */
export function useEnable2FA(options?: Omit<UseMutationOptions<any, Error, void>, 'mutationFn'>) {
  return useMutation({
    mutationFn: () => authApi.enable2FA(),
    ...options,
  });
}

/**
 * Confirm enable 2FA mutation hook
 */
export function useConfirmEnable2FA(
  options?: Omit<UseMutationOptions<any, Error, string>, 'mutationFn'>
) {
  return useMutation({
    mutationFn: (code: string) => authApi.confirmEnable2FA(code),
    ...options,
  });
}

/**
 * Disable 2FA mutation hook
 */
export function useDisable2FA(options?: Omit<UseMutationOptions<any, Error, void>, 'mutationFn'>) {
  return useMutation({
    mutationFn: () => authApi.disable2FA(),
    ...options,
  });
}

/**
 * Send 2FA code mutation hook
 */
export function useSend2FACode(
  options?: Omit<UseMutationOptions<any, Error, string>, 'mutationFn'>
) {
  return useMutation({
    mutationFn: (userId: string) => authApi.send2FACode(userId),
    ...options,
  });
}

/**
 * Verify 2FA mutation hook
 */
export function useVerify2FA(
  options?: Omit<
    UseMutationOptions<AuthResponse, Error, { userId: string; code: string }>,
    'mutationFn'
  >
) {
  const store = useHaulerCentralAuthStore();

  return useMutation<AuthResponse, Error, { userId: string; code: string }>({
    mutationFn: (data: { userId: string; code: string }) =>
      authApi.verify2FA(data.userId, data.code),
    onSuccess: (data) => {
      if (data.success && data.data?.user) {
        store.setUser(data.data.user);
        store.setIsAuthenticated(!!data.data.accessToken);
      }
    },
    ...options,
  });
}

/**
 * Current user query hook
 */
export function useCurrentUser(options?: { enabled?: boolean }) {
  const store = useHaulerCentralAuthStore();

  return useQuery({
    queryKey: ['haulercentral', 'me'],
    queryFn: () => authApi.getCurrentUser(),
    enabled: (options?.enabled ?? store.isAuthenticated) && store.isAuthenticated,
    retry: false,
  });
}

/**
 * OAuth link mutation hook
 */
export function useLinkOAuthAccount(
  provider: 'google' | 'microsoft',
  options?: Omit<
    UseMutationOptions<any, Error, { access_token: string; refresh_token?: string }>,
    'mutationFn'
  >
) {
  return useMutation({
    mutationFn: (tokenData) => authApi.linkOAuthAccount(provider, tokenData),
    ...options,
  });
}

/**
 * OAuth unlink mutation hook
 */
export function useUnlinkOAuthAccount(
  provider: 'google' | 'microsoft',
  options?: Omit<UseMutationOptions<any, Error, void>, 'mutationFn'>
) {
  return useMutation({
    mutationFn: () => authApi.unlinkOAuthAccount(provider),
    ...options,
  });
}
